import { TvInternetOffer } from '@updater/consumer-graph/*';
import { Qual } from 'types/Qual';
import type {
  MicrositeCustomTheme,
  CustomThemeColors,
  BaseColors,
} from 'types/MicrositeTheme';

/**
 * Customer information for the reservation token
 */
export interface ReservationCustomerInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  moveDate?: string;
}

/**
 * Provider information for the reservation token
 */
export interface ReservationProviderInfo {
  id: string;
  code: string;
  logoUrl: string;
}

/**
 * Theme configuration with asset URLs for reservation token
 */
export interface ThemeWithAssets {
  theme: MicrositeCustomTheme;
  logoUrl: string;
  faviconUrls: {
    '32x32': string;
    '192x192': string;
    '180x180': string;
  };
}

/**
 * Slim theme payload for Embed GBF — only what's rendered inside the iframe.
 * No logo or favicon assets needed since the host page controls those.
 */
export interface ThemeWithoutAssets {
  theme: {
    colors: Pick<CustomThemeColors, 'background' | 'progressBar'>;
    baseColors: BaseColors;
  };
}

/**
 * Async config passed to the moving app for guest buyflow
 */
export interface ReservationAsyncConfig {
  supportPhoneNumber?: string;
  themeWithAssets?: ThemeWithAssets | ThemeWithoutAssets;
  source?: string;
}

/**
 * Parameters for generating a reservation token payload
 */
export interface GenerateReservationTokenPayloadParams {
  supportPhoneNumber?: string;
  source?: string;
  themeWithAssets?: ThemeWithAssets | ThemeWithoutAssets;
  customerInfo: ReservationCustomerInfo;
  qual: Qual;
  chosenOffer: TvInternetOffer;
  uuid: string;
  requestId?: string;
  providerInfo: ReservationProviderInfo;
  optionalFieldsById: Record<string, string | undefined>;
}

export interface MovingAppTokenPayload {
  uuid: string;
  requestId: string;
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    moveDate?: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    unit?: string;
  };
  offer: {
    offerCode: string;
    providerOfferId?: string;
    name: string;
    provider: {
      id: string;
      code: string;
      logoUrl: string;
    };
    highlights?: string[];
    extendedHighlights?: string[];
    pricing: {
      charges: Array<{
        amount?: number;
        term?: Record<string, string | number>;
        type?: string;
      }>;
      price: number;
      total: number;
      term?: {
        length?: number;
        unit?: string;
      };
      description?: string;
    };
    products: {
      internet: {
        name: string;
        speed: string;
        downloadSpeed: { value: number; unit: string };
        uploadSpeed: { value: number; unit: string };
        type: string;
        features: string[];
      };
      tv?: { channelCount?: number };
      phone?: { type?: string };
      mobile?: { type?: string };
    };
    planDetails?: {
      instantOn?: boolean;
    };
    options?: {
      installationOptions?: {
        type: 'professional' | 'self';
        amount: number;
      };
      internetEquipmentOptions?: {
        customizations?: Array<{
          choices?: Array<{
            choiceId?: string;
            name?: string;
            description?: string;
            priceList?: Array<{
              amount?: number;
              category?: string;
              recurringInterval?: string;
              term?: {
                unit?: string;
                length?: number;
              };
            }>;
          }>;
        }>;
      };
    };
  };
  asyncConfig?: ReservationAsyncConfig;
  qualification?: {
    optionalFieldsById: Record<string, string>;
  };
}

export function generateReservationTokenPayload({
  supportPhoneNumber,
  source,
  themeWithAssets,
  customerInfo,
  qual,
  chosenOffer,
  uuid,
  requestId,
  providerInfo,
  optionalFieldsById,
}: GenerateReservationTokenPayloadParams): MovingAppTokenPayload {
  const shortTermPricing = chosenOffer?.pricing?.charges?.find(
    (charge) => charge.type === 'short_term'
  );

  const totalPrice =
    chosenOffer?.pricing?.price || shortTermPricing?.amount || 0;

  return {
    uuid,
    requestId,
    qualification: {
      optionalFieldsById,
      ...qual,
    },
    customerInfo: {
      firstName: customerInfo?.firstName || '',
      lastName: customerInfo?.lastName || '',
      email: customerInfo?.email || '',
      phone: customerInfo?.phoneNumber,
      moveDate: customerInfo?.moveDate,
    },
    address: {
      street: qual.street,
      city: qual.city,
      state: qual.state,
      zip: qual.zip?.substring(0, 5),
      unit: qual.unit,
    },
    offer: {
      offerCode: chosenOffer?.code,
      name: chosenOffer?.name,
      provider: providerInfo,
      highlights: chosenOffer?.highlights,
      extendedHighlights: chosenOffer?.extendedHighlights,
      pricing: {
        charges: chosenOffer?.pricing?.charges || [],
        price: totalPrice,
        total: totalPrice,
        term: chosenOffer?.pricing?.term,
        description: chosenOffer?.pricing?.description,
      },
      providerOfferId: chosenOffer?.providerOfferId,
      products: {
        internet: {
          name: chosenOffer?.products?.internet?.name || '',
          speed: chosenOffer?.products?.internet?.downloadSpeed
            ? `${chosenOffer.products.internet.downloadSpeed.value} ${chosenOffer.products.internet.downloadSpeed.unit}`
            : '',
          downloadSpeed: {
            value: chosenOffer?.products?.internet?.downloadSpeed?.value || 0,
            unit:
              chosenOffer?.products?.internet?.downloadSpeed?.unit || 'mbps',
          },
          uploadSpeed: {
            value: chosenOffer?.products?.internet?.uploadSpeed?.value || 0,
            unit: chosenOffer?.products?.internet?.uploadSpeed?.unit || 'mbps',
          },
          type: chosenOffer?.products?.internet?.type || '',
          features: chosenOffer?.products?.internet?.features || [],
        },
        tv: chosenOffer?.products?.tv
          ? {
              channelCount: chosenOffer.products.tv.channels.count,
            }
          : undefined,
        phone: chosenOffer?.products?.phone
          ? {
              type: chosenOffer.products.phone.type,
            }
          : undefined,
        mobile: chosenOffer?.products?.mobile
          ? {
              type: chosenOffer.products.mobile.type,
            }
          : undefined,
      },
      planDetails: {
        // Token only needs presence/absence of instant-on capability, not the full object.
        instantOn: Boolean(chosenOffer?.planDetails?.instantOn) ?? false,
      },
      options: {
        installationOptions: chosenOffer?.options?.installationOptions
          ? {
              type: chosenOffer.options.installationOptions.type,
              amount: chosenOffer.options.installationOptions.amount || 0,
            }
          : undefined,
        internetEquipmentOptions: chosenOffer?.options?.internetEquipmentOptions
          ? {
              customizations:
                chosenOffer.options.internetEquipmentOptions.customizations,
            }
          : undefined,
      },
    },
    asyncConfig: {
      supportPhoneNumber,
      source,
      themeWithAssets,
    },
  };
}
